import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function AssignmentsTab() {
  const [queue, setQueue] = useState([]);
  const [worklists, setWorklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('billing');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qR, wR] = await Promise.all([
        api.get(`/rcm/automation/queue/${role}`),
        api.get('/rcm/automation/worklists'),
      ]);
      setQueue(qR.data.queue || []);
      setWorklists(wR.data.worklists || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  const autoAssign = async () => {
    try { await api.post('/rcm/automation/assign/auto?limit=15'); load(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const generateWorklists = async () => {
    try { await api.post('/rcm/automation/worklists/generate-daily'); load(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const ackItem = async (id) => {
    try { await api.post(`/rcm/automation/assignments/${id}/acknowledge`); load(); } catch {}
  };
  const completeItem = async (id) => {
    try { await api.post(`/rcm/automation/assignments/${id}/complete`, { outcome: 'completed' }); load(); } catch {}
  };

  return (
    <div className="space-y-4" data-testid="assignments-tab">
      <div className="flex gap-2 items-center">
        <select value={role} onChange={e => setRole(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="role-select">
          <option value="billing">Billing</option>
          <option value="provider">Provider</option>
          <option value="coder">Coder</option>
        </select>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={autoAssign} data-testid="auto-assign-btn">Auto-Assign</Button>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={generateWorklists}>Generate Worklists</Button>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
      </div>

      {/* Role Queue */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">{role} Queue ({queue.length})</CardTitle></CardHeader>
        <CardContent className="px-3 pb-3">
          {queue.length === 0 ? <p className="text-xs text-muted-foreground py-3 text-center">No assignments for {role}</p> : (
          <table className="w-full text-xs"><thead><tr className="border-b text-left">
            <th className="py-1 font-medium text-muted-foreground text-right">Priority</th>
            <th className="py-1 font-medium text-muted-foreground">Payer</th>
            <th className="py-1 font-medium text-muted-foreground text-right">Amount</th>
            <th className="py-1 font-medium text-muted-foreground">Reason</th>
            <th className="py-1 font-medium text-muted-foreground">Status</th>
            <th className="py-1"></th>
          </tr></thead><tbody>{queue.slice(0, 20).map((a) => (
            <tr key={a.id} className="border-b hover:bg-muted/30">
              <td className="py-1 text-right font-medium">{a.priority_score?.toFixed?.(0) || a.priority_score}</td>
              <td className="py-1">{a.payer_name || '—'}</td>
              <td className="py-1 text-right">{a.dollar_amount > 0 ? fmt(a.dollar_amount) : '—'}</td>
              <td className="py-1 truncate max-w-[200px] text-muted-foreground">{a.assignment_reason}</td>
              <td className="py-1"><StatusChip status={a.status} size="xs" /></td>
              <td className="py-1 flex gap-1">
                {a.status === 'assigned' && <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1" onClick={() => ackItem(a.id)}><Eye className="h-2.5 w-2.5" /></Button>}
                {['assigned','acknowledged'].includes(a.status) && <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1" onClick={() => completeItem(a.id)}><CheckCircle2 className="h-2.5 w-2.5" /></Button>}
              </td>
            </tr>
          ))}</tbody></table>)}
        </CardContent>
      </Card>

      {/* Worklists */}
      {worklists.length > 0 && (
        <Card>
          <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Generated Worklists</CardTitle></CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {worklists.map((wl) => (
              <div key={wl.id} className="border rounded p-2 text-xs">
                <div className="flex justify-between"><span className="font-medium">{wl.role} — {wl.generated_date}</span><Badge variant="outline" className="text-[10px]">{wl.total_items} items</Badge></div>
                <div className="flex gap-3 mt-1 text-muted-foreground">
                  <span>High: {wl.priority_breakdown?.high || 0}</span>
                  <span>Med: {wl.priority_breakdown?.medium || 0}</span>
                  <span>Low: {wl.priority_breakdown?.low || 0}</span>
                  <span>{fmt(wl.total_dollars_at_risk)} at risk</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// INSIGHTS TAB (Phase 5 — Contract Intelligence + Outcomes)
// ═══════════════════════════════════════

export { AssignmentsTab };
